import { ShoppingBag, ShoppingCart } from 'lucide-react'
import type { ConversationSalesContext } from '@/services/conversationsApi'
import { hasConfirmedProduct } from '@/pages/conversations/conversationUx'

interface ActiveOrderDraftCardProps {
  conversationId?: string
  salesContext?: ConversationSalesContext | null
  canViewAllConversations?: boolean
  onCreateOrder: () => void
}

export function ActiveOrderDraftCard({
  salesContext,
  onCreateOrder,
}: ActiveOrderDraftCardProps) {
  if (!salesContext) return null

  const activeDraft = salesContext.activeOrderDraft
  const draftItems = activeDraft?.items || []
  const hasItems = draftItems.length > 0
  const productName = salesContext.lastMentionedProductName || salesContext.product?.title || ''
  const productPrice = salesContext.product?.price ? `$${Number(salesContext.product.price).toFixed(2)}` : ''
  const totalCents = activeDraft?.totalCents
  const totalDisplay = totalCents ? `$${(totalCents / 100).toFixed(2)}` : ''
  const fulfillmentAddress = activeDraft?.fulfillmentAddress
  const isProductConfirmed = hasConfirmedProduct(salesContext)

  // Si no hay items, ni producto en foco, ni dirección, no renderizamos el bloque de carrito
  if (!hasItems && !productName && !fulfillmentAddress) {
    return null
  }

  return (
    <div
      role="region"
      aria-label="Detalle del borrador de orden"
      className="mt-2.5 rounded-xl border border-emerald-200/80 bg-white/90 p-3 shadow-2xs backdrop-blur-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Left side: Products and Fulfillment */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-700 min-w-0">
          {hasItems ? (
            <div className="flex flex-wrap items-center gap-1.5 font-semibold text-emerald-950">
              <span className="flex items-center gap-1 text-emerald-800">
                <ShoppingBag className="h-3.5 w-3.5" />
                <span>Carrito:</span>
              </span>
              {draftItems.map((it, idx) => (
                <span
                  key={idx}
                  className="rounded-md border border-emerald-200 bg-emerald-50/70 px-2 py-0.5 font-medium text-emerald-900"
                >
                  {it.quantity || 1}x {it.title || 'Producto'}
                  {it.unitPriceCents ? ` ($${(it.unitPriceCents / 100).toFixed(2)})` : ''}
                </span>
              ))}
            </div>
          ) : productName ? (
            <div className="flex items-center gap-1.5 font-semibold text-emerald-950">
              <ShoppingBag className="h-3.5 w-3.5 text-emerald-700" />
              <span>Producto en foco:</span>
              <span className="rounded-md border border-emerald-200 bg-emerald-50/70 px-2 py-0.5 font-medium text-emerald-900">
                {productName} {productPrice ? `(${productPrice})` : ''}
              </span>
            </div>
          ) : null}

          {fulfillmentAddress && (
            <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700 font-medium">
              📍 {fulfillmentAddress}
            </span>
          )}
        </div>

        {/* Right side: Total and CTA Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {totalDisplay && (
            <div className="text-right">
              <span className="text-[10px] uppercase font-bold text-slate-500 block">Total</span>
              <span className="text-sm font-black text-emerald-900">{totalDisplay}</span>
            </div>
          )}

          <button
            type="button"
            onClick={onCreateOrder}
            disabled={!isProductConfirmed}
            title={
              isProductConfirmed
                ? 'Completar o editar orden comercial'
                : 'Se requiere un producto confirmado en la conversación para emitir la orden'
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#075f55] px-3 py-1.5 text-xs font-bold text-white shadow-xs shadow-[#075f55]/20 transition-all hover:bg-[#064e46] hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            <span>Crear Orden {totalDisplay ? `(${totalDisplay})` : ''}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
